import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/common";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { name: "", email: "", password: "", phone: "", education_level: "", subject: "", specialization: "",
  qualification: "", experience: 0, bio: "", profile_picture: "", initial_credits: 0 };

export function UserFormDialog({ role, user, open, onClose, onSaved }) {
  const editing = !!user;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [lastKey, setLastKey] = useState(null);
  const key = open ? user?.id || "new" : null;
  if (key !== lastKey) {
    setLastKey(key);
    if (open) setF(editing ? { ...EMPTY, ...user, password: "" } : EMPTY);
  }
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const fields = role === "tutor"
    ? [["subject", "Subject"], ["specialization", "Specialization"], ["qualification", "Qualification"], ["experience", "Experience (years)", "number"]]
    : [["education_level", "Education level"]];

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, experience: Number(f.experience) || 0, initial_credits: Number(f.initial_credits) || 0 };
      if (editing) {
        const { password, initial_credits, id, role: _r, ...rest } = body;
        const allowed = ["name", "email", "phone", "education_level", "subject", "specialization", "qualification", "experience", "bio", "profile_picture"];
        await api.put(`/admin/users/${user.id}`, Object.fromEntries(allowed.filter((k) => k in rest).map((k) => [k, rest[k] ?? ""])));
      } else {
        await api.post("/admin/users", { ...body, role });
      }
      toast.success(editing ? "Changes saved" : `${role === "tutor" ? "Tutor" : "Student"} created`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${user.name}` : `Add ${role}`}</DialogTitle>
          <DialogDescription>{editing ? "Update profile information." : "Admin-created accounts are verified and approved automatically."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ImageUpload value={f.profile_picture} name={f.name} onChange={(v) => setF({ ...f, profile_picture: v })} testid="user-form-photo" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={set("name")} data-testid="user-form-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="user-form-email" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="user-form-phone" /></div>
            {!editing && <div className="space-y-1.5"><Label>Temporary password</Label><Input type="password" value={f.password} onChange={set("password")} data-testid="user-form-password" /></div>}
            {fields.map(([k, l, t]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type={t || "text"} value={f[k] ?? ""} onChange={set(k)} data-testid={`user-form-${k}`} /></div>
            ))}
            {!editing && role === "student" && (
              <div className="space-y-1.5"><Label>Initial credits</Label><Input type="number" min={0} value={f.initial_credits} onChange={set("initial_credits")} data-testid="user-form-initial-credits" /></div>
            )}
          </div>
          {role === "tutor" && <div className="space-y-1.5"><Label>Bio</Label><Textarea value={f.bio} onChange={set("bio")} data-testid="user-form-bio" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="user-form-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1F6FFF] hover:bg-[#1450C9]" data-testid="user-form-save">{busy && <Spinner />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CreditDialog({ student, onClose, onSaved }) {
  const [f, setF] = useState({ action: "add", amount: 100, reason: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/credits/${student.id}`, { ...f, amount: Number(f.amount) });
      toast.success("Credits updated");
      setF({ action: "add", amount: 100, reason: "" });
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="credit-dialog">
        <DialogHeader>
          <DialogTitle>Manage credits · {student?.name}</DialogTitle>
          <DialogDescription>Available: {student?.wallet?.available ?? 0} · Reserved: {student?.wallet?.reserved ?? 0}. Every change is audited.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 rounded-full bg-slate-100 p-1 text-sm font-semibold">
          {[["add", "Add credits"], ["adjust", "Adjust (+/-)"]].map(([v, l]) => (
            <button key={v} onClick={() => setF({ ...f, action: v })} data-testid={`credit-action-${v}`}
              className={`rounded-full py-2 ${f.action === v ? "bg-[#0B1A33] text-white" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-1.5"><Label>{f.action === "add" ? "Amount to add" : "Adjustment (negative to reduce)"}</Label>
          <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="credit-amount-input" /></div>
        <div className="space-y-1.5"><Label>Reason</Label>
          <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Monthly package" data-testid="credit-reason-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#3CC34A] text-[#0B1A33] hover:bg-[#2DA638]" data-testid="credit-submit-button">{busy && <Spinner />} Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
